import axios from 'axios';
import React, { useContext, useEffect, useState, useCallback } from 'react';
import styles from './StayUpdated.module.css';
import IsAuth from '../../createContext/is-Auth/IsAuthContext';
import { ProfileContext } from '../../createContext/Profile/ProfileContext';
import { Link } from 'react-router-dom';

const Backend_URL = process.env.REACT_APP_BACKEND_URL;

const StayUpdated = () => {
  const [showMore, setShowMore] = useState(false);
  const [players, setTopPeople] = useState([]);
  const [loadingStates, setLoadingStates] = useState({});
  const { user } = useContext(IsAuth);
  const { toggleFollow } = useContext(ProfileContext);

  const getInitialFollowStatus = useCallback((player) => {
    if (user.following?.some(followed => followed?._id === player._id)) {
      return 'following';
    } else if (player.followRequests?.includes(user._id)) {
      return 'requested';
    }
    return 'none';
  }, [user]);

  const fetchData = useCallback(async () => {
    try {
      const response = await axios.get(`${Backend_URL}/api/getusers/`);
      const filteredUsers = response.data
        .filter(player => player._id !== user._id)
        .map(player => ({
          ...player,
          followStatus: getInitialFollowStatus(player),
        }))
        .filter(player => player.followStatus === 'none');
      setTopPeople(filteredUsers);
    } catch (err) {
      console.error("Error fetching users:", err);
      setTopPeople([]);
    }
  }, [user, getInitialFollowStatus]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleFollow = async (userId) => {
    setLoadingStates(prev => ({ ...prev, [userId]: true }));

    const targetPlayer = players.find(p => p._id === userId);
    const currentStatus = targetPlayer.followStatus;

    // Optimistically update the UI
    setTopPeople(prevPeople =>
      prevPeople.map(player =>
        player._id === userId
          ? {
              ...player,
              followStatus: player.isPrivate && currentStatus === 'none' ? 'requested' : 'following',
              followers: player.followers + (currentStatus === 'none' ? 1 : 0)
            }
          : player
      )
    );

    try {
      const response = await toggleFollow(userId);
      
      setTopPeople(prevPeople =>
        prevPeople.map(player => {
          if (player._id !== userId) return player;

          let newStatus;
          let followersDelta = 0;

          if (currentStatus === 'following') {
            newStatus = 'none';
            followersDelta = -1;
          } else if (response?.data?.status === 'followed') {
            newStatus = 'following';
            followersDelta = 1;
          } else if (response?.data?.status === 'requested' || response?.data?.status === 'pending') {
            newStatus = 'requested';
            followersDelta = 0;
          } else {
            newStatus = 'none';
            followersDelta = 0;
          }

          return {
            ...player,
            followStatus: newStatus,
            followers: player.followers + followersDelta
          };
        })
      );
    } catch (error) {
      // Revert the optimistic update on error
      setTopPeople(prevPeople =>
        prevPeople.map(player =>
          player._id === userId
            ? {
                ...player,
                followStatus: currentStatus,
                followers: player.followers - (currentStatus === 'none' ? 1 : 0)
              }
            : player
        )
      );
      console.error("Error toggling follow:", error);
    } finally {
      setLoadingStates(prev => ({ ...prev, [userId]: false }));
    }
  };

  const renderFollowButton = (player) => {
    const isLoading = loadingStates[player._id];
    
    if (isLoading) {
      return (
        <button 
          className={styles.followButton}
          disabled
        >
          ...
        </button>
      );
    }

    switch (player.followStatus) {
      case 'following':
        return (
          <button 
            className={`${styles.followButton} ${styles.followingButton}`}
            onClick={() => handleFollow(player._id)}
          >
            Unfollow
          </button>
        );
      case 'requested':
        return (
          <button 
            className={`${styles.followButton} ${styles.requestedButton}`}
            disabled
          >
            Requested
          </button>
        );
      default:
        return (
          <button 
            className={styles.followButton}
            onClick={() => handleFollow(player._id)}
          >
            {player.isPrivate ? 'Request to Follow' : 'Follow'}
          </button>
        );
    }
  };

  // Memoize sorted players to prevent unnecessary re-sorting
  const sortedPlayers = React.useMemo(() => 
    [...players].sort((a, b) => b.followers - a.followers),
    [players]
  );
  
  const displayedPlayers = showMore ? sortedPlayers.slice(0, 5) : sortedPlayers.slice(0, 2);

  if (players.length === 0) return null;

  return (
    <section className={styles.stayUpdated}>
      <h2 className={styles.sectionTitle}>Stay updated with</h2>
      
      {displayedPlayers.map((player) => (
        <div key={player._id} className={styles.userSuggestion}>
          <img 
            src={player.avatar} 
            alt={`${player.name || 'User'}'s avatar`} 
            className={styles.userAvatar}
          />
          <div className={styles.userInfo}>
            <Link to={`/profile/${player.name}`}>
              <h3 className={styles.userName}>{player.realName || 'Anonymous User'}</h3>
            </Link>
            <p className={styles.userHandle}>@{player.name || player._id.slice(0, 8)}</p>
          </div>
          {renderFollowButton(player)}
        </div>
      ))}
      
      {players.length > 2 && (
        <div 
          className={styles.matchDate} 
          onClick={() => setShowMore(!showMore)}
          style={{ cursor: 'pointer' }}
        >
          {showMore ? 'Show Less' : 'Show More'}
        </div>
      )}
    </section>
  );
};

export default StayUpdated;